import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Loader2 } from "lucide-react";
import Footer from "../components/Footer";
import { TYPE_META } from "../components/ProductPicker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * "Shop by Product" — entry funnel for gift-shoppers who walk in with a
 * product idea (e.g. "I want a mug") instead of a style idea. Lists every
 * active style across all collections; clicking a style routes to
 * /style/{slug}?product={productType} which pre-selects the right product
 * on the style landing page.
 */

const PRODUCT_HEROES = {
  frame: {
    headline: "Premium framed pet portraits",
    subhead: "Hand-printed, gallery-quality framed art delivered to your door.",
  },
  canvas: {
    headline: "Gallery-wrap canvas prints",
    subhead: "Statement-sized canvas with rich texture and depth.",
  },
  acrylic: {
    headline: "Acrylic display stands",
    subhead: "Free-standing portraits for desks, mantles, and gifting.",
  },
  mug: {
    headline: "Custom pet coffee mugs",
    subhead: "Your pet, sublimation-printed onto a premium 11oz ceramic mug.",
  },
};

const ShopByProductPage = () => {
  const { productType } = useParams();
  const navigate = useNavigate();
  const [collections, setCollections] = useState(null);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        const [catalog, sizes] = await Promise.all([
          axios.get(`${API}/styles/catalog`, { signal: ctrl.signal }),
          axios.get(`${API}/sizes`, { signal: ctrl.signal }),
        ]);
        setCollections(catalog.data?.collections || []);
        setProducts(sizes.data?.items || []);
      } catch {
        setCollections([]);
      }
    })();
    return () => ctrl.abort();
  }, []);

  // Validate productType against the live catalogue. If a customer hits a URL
  // for a type that isn't available, send them back home.
  const isValidType = useMemo(
    () => (products || []).some((p) => p.product_type === productType),
    [products, productType]
  );

  // Pick the cheapest "from ₹X" price for this product across collections so
  // the hero can display a starting price. Declared before any early return
  // so hook order stays stable across renders.
  const fromPrice = useMemo(() => {
    const product = (products || []).find((p) => p.product_type === productType);
    if (!product || !collections) return null;
    const prices = collections
      .map((c) => (c.prices && c.prices[product.id]) || c.price)
      .filter((n) => typeof n === "number" && n > 0);
    if (prices.length === 0) return null;
    return Math.min(...prices);
  }, [collections, products, productType]);

  const meta = TYPE_META[productType] || {};
  const hero = PRODUCT_HEROES[productType] || {};

  if (collections === null || !products.length) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#C8A27A]" />
      </div>
    );
  }

  if (!isValidType) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <p className="text-[#6B6B6B]">This product isn&apos;t available right now.</p>
        <Link to="/" className="underline" data-testid="shop-by-product-back-home">
          Back home
        </Link>
      </div>
    );
  }

  // Flat list of every active style, grouped under its collection. Each card
  // links to /style/{slug}?product={productType}.
  const allStyles = collections.flatMap((c) =>
    (c.styles || []).map((s) => ({ ...s, _collection: c }))
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="glass-header py-5">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 flex items-center justify-between">
          <Link
            to="/"
            className="serif text-2xl tracking-tight text-[#1A1A1A] font-semibold"
            data-testid="shop-page-brand"
          >
            theSnugglePrints
          </Link>
          <Link
            to="/"
            className="text-sm text-[#6B6B6B] hover:text-[#1A1A1A]"
          >
            All products →
          </Link>
        </div>
      </header>

      <main className="pt-24 pb-12">
        <section className="max-w-6xl mx-auto px-6 lg:px-10 mb-12">
          <p className="label-uppercase mb-3 text-[#6B6B6B]" data-testid="shop-page-breadcrumb">
            Shop by Product / {meta.label || productType}
          </p>
          <h1
            className="serif text-4xl sm:text-6xl text-[#1A1A1A] leading-[1.05] mb-5"
            data-testid="shop-page-headline"
          >
            {hero.headline || `Custom Pet ${meta.label || productType}`}
          </h1>
          <p className="text-[#3A3A3A] text-lg mb-6 max-w-2xl">
            {hero.subhead}
          </p>
          {fromPrice !== null && (
            <p
              className="text-sm text-[#9B6A2D]"
              data-testid="shop-page-from-price"
            >
              From ₹{Math.round(fromPrice).toLocaleString("en-IN")}
            </p>
          )}
        </section>

        <section className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="flex items-baseline justify-between mb-6">
            <h2 className="serif text-2xl sm:text-3xl text-[#1A1A1A]">
              Pick a style
            </h2>
            <span className="text-xs label-uppercase text-[#6B6B6B]">
              {allStyles.length} options
            </span>
          </div>

          <div
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3"
            data-testid="shop-page-styles-grid"
          >
            {allStyles.map((style) => (
              <Link
                key={style.id}
                to={`/style/${style.slug || style.id}?product=${productType}`}
                className="bg-card border border-[#EAE5DF] hover:border-[#C8A27A] transition-colors group"
                data-testid={`shop-style-card-${style.id}`}
                onClick={() => {
                  // Lightweight analytics — fire-and-forget.
                  try {
                    axios.post(`${API}/events`, {
                      event_type: "shop_by_product_click",
                      metadata: { product_type: productType, style_id: style.id },
                    });
                  } catch {
                    /* ignore */
                  }
                  void navigate;
                }}
              >
                <div className="aspect-[4/5] overflow-hidden bg-[#F3EEE6] flex items-center justify-center">
                  {style.thumbnail_url ? (
                    <img
                      src={style.thumbnail_url}
                      alt={style.name}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
                    />
                  ) : (
                    <span className="serif text-xl text-[#9B6A2D] px-3 text-center">
                      {style.name}
                    </span>
                  )}
                </div>
                <div className="px-3 py-3">
                  <p className="text-[10px] uppercase tracking-wider text-[#6B6B6B] mb-1">
                    {style._collection?.name || ""}
                  </p>
                  <p className="font-medium text-[#1A1A1A] group-hover:text-[#9B6A2D] transition-colors">
                    {style.name}
                  </p>
                  <p className="text-xs text-[#6B6B6B] mt-1 inline-flex items-center gap-1">
                    Choose this style
                    <ArrowRight className="h-3 w-3" />
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ShopByProductPage;
